import { CheckCircle2, Clock, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Where a listing stands with the reviewer.
 *
 * Products now need admin approval before shoppers see them, and a vendor whose
 * product is silently invisible will conclude the platform is broken rather than
 * that it is waiting. The awkward case is a live product with a queued edit: it IS
 * selling, on the previously approved version, while the change waits — so the
 * badge says both rather than picking one.
 */
export function ProductApprovalBadge({
  product,
}: {
  product: {
    approval_status: 'PENDING' | 'APPROVED' | 'REJECTED';
    approval_note: string | null;
    has_pending_changes: boolean;
    status_label: string;
  };
}) {
  const { approval_status, approval_note, has_pending_changes, status_label } = product;

  const tone =
    approval_status === 'REJECTED'
      ? 'bg-destructive/10 text-destructive'
      : approval_status === 'PENDING'
        ? 'bg-warning/10 text-warning-foreground'
        : has_pending_changes
          ? 'bg-warning/10 text-warning-foreground'
          : 'bg-success/10 text-success';

  const Icon =
    approval_status === 'REJECTED' ? XCircle : approval_status === 'APPROVED' && !has_pending_changes ? CheckCircle2 : Clock;

  return (
    <div className="mt-3" data-testid="approval-badge">
      <span
        className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold', tone)}
        data-testid={`approval-status-${approval_status}`}
      >
        <Icon className="h-3.5 w-3.5" />
        {status_label}
      </span>

      {/* The reviewer's reason is the only thing that tells a vendor what to change. */}
      {approval_status === 'REJECTED' && approval_note && (
        <p className="mt-1.5 text-xs text-destructive" data-testid="approval-note">
          {approval_note}
        </p>
      )}
    </div>
  );
}
